import React, { useState, useEffect } from 'react';
import {
    Box,
    Button,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    Typography,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    TextField,
    Grid,
    IconButton,
    Snackbar,
    Alert,
    Stack,
    useTheme,
    useMediaQuery
} from '@mui/material';
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon } from '@mui/icons-material';
import apiClient from '../../api';
import { useAuth } from '../../context/AuthContext';

function CompanyManagement() {
    const { user } = useAuth();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const isAdmin = user?.role === 'admin';
    // State for companies data
    const [companies, setCompanies] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    
    // State for dialog
    const [openDialog, setOpenDialog] = useState(false);
    const [dialogType, setDialogType] = useState('add'); // 'add', 'edit', 'delete'
    const [currentCompany, setCurrentCompany] = useState(null);
    const [formData, setFormData] = useState({
        name: '',
        address: {
            street: '',
            city: '',
            state: '',
            zip: ''
        },
        phone: '',
        notificationEmail: '',
        intakeTestingBanner: false
    });
    
    // Pagination state
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    
    // Snackbar state
    const [snackbar, setSnackbar] = useState({
        open: false,
        message: '',
        severity: 'success'
    });

    // Fetch companies data
    const fetchCompanies = async () => {
        setLoading(true);
        try {
            if (isAdmin) {
                const response = await apiClient.get('/admin/companies');
                setCompanies(response.data.data.companies);
                setError(null);
            } else {
                // Company admins only see their own company (from auth context)
                const companyData = user && user.company ? [user.company] : [];
                setCompanies(companyData);
                setError(null);
            }
        } catch (err) {
            console.error('Error fetching companies:', err);
            setError('Failed to fetch companies. Please try again later.');
        } finally {
            setLoading(false);
        }
    };

    // Initial data fetch
    useEffect(() => {
        fetchCompanies();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isAdmin, user]);

    // Handle dialog open/close
    const handleOpenDialog = (type, company = null) => {
        setDialogType(type);
        setCurrentCompany(company);
        
        if (type === 'add') {
            setFormData({
                name: '',
                address: {
                    street: '',
                    city: '',
                    state: '',
                    zip: ''
                },
                phone: '',
                notificationEmail: ''
            });
        } else if (type === 'edit' && company) {
            setFormData({
                name: company.name || '',
                address: {
                    street: company.address?.street || '',
                    city: company.address?.city || '',
                    state: company.address?.state || '',
                    zip: company.address?.zip || ''
                },
                phone: company.phone || '',
                notificationEmail: company.notificationEmail || '',
                intakeTestingBanner: !!company.intakeTestingBanner
            });
        }
        
        setOpenDialog(true);
    };

    const handleCloseDialog = () => {
        setOpenDialog(false);
    };

    // Handle form submission
    const handleSubmit = async () => {
        try {
            if (!isAdmin) {
                return; // Company admins cannot modify company records here
            }
            if (dialogType === 'add') {
                await apiClient.post('/admin/companies', formData);
                setSnackbar({
                    open: true,
                    message: 'Company added successfully',
                    severity: 'success'
                });
            } else if (dialogType === 'edit' && currentCompany) {
                await apiClient.patch(`/admin/companies/${currentCompany._id}`, formData);
                setSnackbar({
                    open: true,
                    message: 'Company updated successfully',
                    severity: 'success'
                });
            } else if (dialogType === 'delete' && currentCompany) {
                await apiClient.delete(`/admin/companies/${currentCompany._id}`);
                setSnackbar({
                    open: true,
                    message: 'Company deleted successfully',
                    severity: 'success'
                });
            }
            
            // Refresh data and close dialog
            fetchCompanies();
            handleCloseDialog();
        } catch (err) {
            console.error('Error:', err);
            setSnackbar({
                open: true,
                message: err.response?.data?.message || 'An error occurred',
                severity: 'error'
            });
        }
    };

    // Handle form input changes
    const handleInputChange = (e) => {
        const { name, value } = e.target;
        
        if (name.includes('.')) {
            const [parent, child] = name.split('.');
            setFormData({
                ...formData,
                [parent]: {
                    ...formData[parent],
                    [child]: value
                }
            });
        } else {
            setFormData({
                ...formData,
                [name]: value
            });
        }
    };

    // Handle pagination
    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    // Handle snackbar close
    const handleCloseSnackbar = () => {
        setSnackbar({
            ...snackbar,
            open: false
        });
    };

    return (
        <Box sx={{ px: { xs: 2, md: 3 } }}>
            {/* Header aligned to match User Management spacing */}
            <Stack 
                direction={{ xs: 'column', sm: 'row' }} 
                justifyContent="space-between" 
                alignItems={{ xs: 'flex-start', sm: 'center' }} 
                spacing={2}
                sx={{ mb: 3 }}
            >
                <Box>
                    <Typography variant="h5" sx={{ fontWeight: 600, mb: 1 }}>
                        Company Management
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Manage companies and their information
                    </Typography>
                </Box>
                {isAdmin && (
                    <Button 
                        variant="contained" 
                        startIcon={<AddIcon />}
                        onClick={() => handleOpenDialog('add')}
                        size={isMobile ? 'small' : 'medium'}
                    >
                        Add Company
                    </Button>
                )}
            </Stack>

            {loading ? (
                <Typography>Loading companies...</Typography>
            ) : error ? (
                <Typography color="error">{error}</Typography>
            ) : (
                <Paper sx={{ width: '100%', overflow: 'hidden' }}>
                    <TableContainer>
                        <Table stickyHeader aria-label="companies table">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Name</TableCell>
                                    <TableCell>Address</TableCell>
                                    <TableCell>Phone</TableCell>
                                    {isAdmin && <TableCell align="right">Actions</TableCell>}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {companies
                                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                                    .map((company) => (
                                        <TableRow key={company._id}>
                                            <TableCell>{company.name}</TableCell>
                                            <TableCell>
                                                {company.address?.street && (
                                                    <>
                                                        {company.address.street}, {company.address.city}, 
                                                        {company.address.state} {company.address.zip}
                                                    </>
                                                )}
                                            </TableCell>
                                            <TableCell>{company.phone}</TableCell>
                                            {isAdmin && (
                                                <TableCell align="right">
                                                    <IconButton 
                                                        color="primary"
                                                        onClick={() => handleOpenDialog('edit', company)}
                                                    >
                                                        <EditIcon />
                                                    </IconButton>
                                                    <IconButton 
                                                        color="error"
                                                        onClick={() => handleOpenDialog('delete', company)}
                                                    >
                                                        <DeleteIcon />
                                                    </IconButton>
                                                </TableCell>
                                            )}
                                        </TableRow>
                                    ))}
                                {companies.length === 0 && (
                                    <TableRow>
                                        <TableCell colSpan={isAdmin ? 4 : 3} align="center">
                                            No companies found
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    <TablePagination
                        rowsPerPageOptions={[5, 10, 25]}
                        component="div"
                        count={companies.length}
                        rowsPerPage={rowsPerPage}
                        page={page}
                        onPageChange={handleChangePage}
                        onRowsPerPageChange={handleChangeRowsPerPage}
                    />
                </Paper>
            )}

            {/* Add/Edit Company Dialog */}
            <Dialog open={openDialog && (dialogType === 'add' || dialogType === 'edit')} onClose={handleCloseDialog}>
                <DialogTitle>
                    {dialogType === 'add' ? 'Add New Company' : 'Edit Company'}
                </DialogTitle>
                <DialogContent>
                    <Grid container spacing={2} sx={{ mt: 1 }}>
                        <Grid item xs={12}>
                            <TextField
                                name="name"
                                label="Company Name"
                                fullWidth
                                required
                                value={formData.name}
                                onChange={handleInputChange}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                name="address.street"
                                label="Street Address"
                                fullWidth
                                value={formData.address.street}
                                onChange={handleInputChange}
                            />
                        </Grid>
                        <Grid item xs={4}>
                            <TextField
                                name="address.city"
                                label="City"
                                fullWidth
                                value={formData.address.city}
                                onChange={handleInputChange}
                            />
                        </Grid>
                        <Grid item xs={4}>
                            <TextField
                                name="address.state"
                                label="State"
                                fullWidth
                                value={formData.address.state}
                                onChange={handleInputChange}
                            />
                        </Grid>
                        <Grid item xs={4}>
                            <TextField
                                name="address.zip"
                                label="ZIP Code"
                                fullWidth
                                value={formData.address.zip}
                                onChange={handleInputChange}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                name="phone"
                                label="Phone Number"
                                fullWidth
                                value={formData.phone}
                                onChange={handleInputChange}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                name="notificationEmail"
                                label="Notification Email (for Intake)"
                                placeholder="support@mycompany.com"
                                fullWidth
                                value={formData.notificationEmail}
                                onChange={handleInputChange}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <Stack direction="row" alignItems="center" spacing={1}>
                                <Typography variant="body2">Show TEST banner in intake emails</Typography>
                                <Button
                                    variant={formData.intakeTestingBanner ? 'contained' : 'outlined'}
                                    color={formData.intakeTestingBanner ? 'warning' : 'inherit'}
                                    size="small"
                                    onClick={() => setFormData({ ...formData, intakeTestingBanner: !formData.intakeTestingBanner })}
                                >
                                    {formData.intakeTestingBanner ? 'On' : 'Off'}
                                </Button>
                            </Stack>
                            <Typography variant="caption" color="text.secondary">
                                When enabled, emails will include a prominent red TEST banner indicating non-production submissions.
                            </Typography>
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseDialog}>Cancel</Button>
                    <Button 
                        onClick={handleSubmit} 
                        variant="contained"
                        disabled={!formData.name}
                    >
                        {dialogType === 'add' ? 'Add' : 'Save'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Delete Confirmation Dialog */}
            <Dialog open={openDialog && dialogType === 'delete'} onClose={handleCloseDialog}>
                <DialogTitle>Delete Company</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Are you sure you want to delete {currentCompany?.name}? 
                        This action cannot be undone, and associated users will be deactivated.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseDialog}>Cancel</Button>
                    <Button onClick={handleSubmit} color="error" variant="contained">
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Snackbar for notifications */}
            <Snackbar 
                open={snackbar.open} 
                autoHideDuration={6000} 
                onClose={handleCloseSnackbar}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            >
                <Alert 
                    onClose={handleCloseSnackbar} 
                    severity={snackbar.severity} 
                    sx={{ width: '100%' }}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
}

export default CompanyManagement; 